// Rapportvakt — admission test and guardrails for the Punktlista visuals.
//
// This module decides whether a point's dormant `visual` field EARNS a drawn
// visual. It never draws anything and it never invents or recomputes a
// number: every check reads fields that are already authored on the point (see
// the EDITORIAL CONTRACT at the top of src/config/rapportvakt.js) and reports
// on them. Pure, dependency-free, no React, no console output, so it can be
// imported from a vitest test or a plain `node --input-type=module -e`
// one-liner with nothing else loaded.
//
// The five checks, in order of how `evaluatePoint` combines them:
//   1. comparison — hasComparison(visual): does it show a quantity against
//      something else (a benchmark, a target, 2+ plotted rows/columns/
//      groups/items, or a range with two endpoints), rather than reporting a
//      lone value with nothing to compare it to. This replaced a hardcoded
//      "countQuantities(visual) >= 3" floor — see hasComparison's doc
//      comment for why that floor was indefensible.
//   2. spatial relation — relationType(visual) === 'spatial' (not 'sequential')
//   3. adds new information — a drawn valueLabel is not already in the prose
//   4. label consistency — every plotted value's drawn label parses back to
//      the same number it's plotted from — see labelConsistencyViolations'
//      doc comment
//   5. word budget — furniture labels/status/step lines stay within their
//      limits; a data string (see classifyDrawnStrings) is never trimmed for
//      a word budget
//
// A verbatim-against-the-source-PDF check used to sit here too (as
// `verbatimViolations().notInAnyAuthoredField`), gating eligibility on every
// drawn string tracing back to some OTHER authored field on the point. It
// was removed for two reasons. First, it fought FIX #3's own `addsNew`
// check: `addsNewInformation` requires at least one drawn value that impact
// and lead do NOT contain, while the verbatim check required every drawn
// string to already be a substring of impact/lead/quote/detail/headline/
// citationShort/hero/facts — a chart earning its place by adding information
// can never satisfy a rule that demands the same information already be
// authored elsewhere on the point. Second, and more fundamentally, its
// anchor was wrong: figures are copied verbatim from the SOURCE REPORT (the
// PDF), not from the point's own prose — and that prose, plus hero/facts, is
// itself a shortened re-cut of the report, not the report. CI cannot open
// the source PDF, so it cannot verify a figure against it, and it must stop
// pretending to. `verbatimViolations().notInProse` stays as a reported
// editorial signal (never a failure) precisely because that guarantee — did
// this number actually appear, verbatim, in the source PDF — is a human
// editorial responsibility, not something this module can check.
//
// STRUCTURAL NOTE — points are independent, there is no competition.
//
// A delivery's three points do not compete for a single visual: each point
// earns (or does not earn) its own visual purely on its own merits. There is
// no per-delivery quota and nothing here ever picks a "winner" among a
// delivery's points. `evaluateDelivery` exists only to batch `evaluatePoint`
// across a delivery's points and report simple counts.
//
// `departureScore` (see below) used to exist to rank a delivery's candidates
// against each other so a quota-of-one could pick the best. With independent
// points there is nothing left to rank. It now serves exactly one purpose: an
// advisory strength signal shown to the author alongside an eligible point,
// because the workflow is that this module suggests and a human confirms. A
// null score (a point that qualifies but genuinely cannot be scored — see
// hgf-2 and trafa-3 in the invariant test) must never block eligibility or
// rendering; `eligible` never reads `score`, and never has.
//
// A point can opt in or out of rendering via `visual.show` (true forces
// rendering when the kind has a renderer; false suppresses it outright), read
// but not authored anywhere in the real config yet — see `visualOutcome` and
// `evaluatePoint`'s `optIn` field below.

/** Collapse whitespace runs to a single space and trim. Case and punctuation
 * are left untouched on purpose: the verbatim check exists to catch numbers
 * and wording that were quietly changed, and lowercasing would hide that. */
function normalizeWs(value) {
  return String(value).replace(/\s+/g, ' ').trim();
}

function isNonEmptyString(value) {
  return typeof value === 'string' && value.length > 0;
}

function wordCount(value) {
  const trimmed = normalizeWs(value);
  if (!trimmed) return 0;
  return trimmed.split(' ').length;
}

/**
 * Parse the leading number out of a string like '154 %', 'drygt 2 %' or
 * '4 766 kr/m²'. Handles Swedish thousands spaces and comma decimals. Numbers
 * that are already numbers pass through unchanged. Returns null, never
 * throws, when nothing numeric is found.
 */
function parseNumber(raw) {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw !== 'string') return null;
  const match = raw.match(/-?\d[\d\s ]*(?:,\d+)?/);
  if (!match) return null;
  const cleaned = match[0].replace(/[\s ]/g, '').replace(',', '.');
  const num = parseFloat(cleaned);
  return Number.isNaN(num) ? null : num;
}

// --- Per-kind shape helpers --------------------------------------------------
// Each visual kind has its own schema (see src/config/rapportvakt.js). These
// helpers extract, per kind, the pieces countQuantities/drawnStrings/
// wordBudgetViolations/plottedItems (used by headlineValue and
// referenceValue, see the departureScore section below) each need, so those
// functions stay a plain per-kind switch instead of repeating field lookups.

function isGroupedColumns(visual) {
  return Array.isArray(visual?.groups);
}

// --- countQuantities ---------------------------------------------------------

/**
 * How many drawn values or positions a visual carries. This used to double as
 * the "is there enough here to justify a chart" floor (>= 3); it no longer
 * gates eligibility — see hasComparison below — and is kept purely as
 * reported signal.
 */
export function countQuantities(visual) {
  if (!visual || typeof visual !== 'object') return 0;
  switch (visual.kind) {
    case 'columns': {
      let count = Array.isArray(visual.columns) ? visual.columns.length : 0;
      if (isGroupedColumns(visual)) {
        count += visual.groups.reduce(
          (sum, group) => sum + (Array.isArray(group?.values) ? group.values.length : 0),
          0
        );
      }
      if (visual.benchmark) count += 1;
      return count;
    }
    case 'bars':
      return Array.isArray(visual.rows) ? visual.rows.length : 0;
    case 'split':
      return Array.isArray(visual.rows) ? visual.rows.length : 0;
    case 'delta':
      return Array.isArray(visual.items) ? visual.items.length : 0;
    case 'gap': {
      // Count what the visual actually plots: the value, the target, and a
      // benchmark if present — not a hardcoded 2. A gap with no target (rare,
      // possibly malformed) should not silently claim to plot two things.
      let count = 0;
      if (visual.value !== undefined && visual.value !== null) count += 1;
      if (visual.target !== undefined && visual.target !== null) count += 1;
      if (visual.benchmark) count += 1;
      return count;
    }
    case 'steps':
      return Array.isArray(visual.steps) ? visual.steps.length : 0;
    default:
      return 0;
  }
}

// --- hasComparison ------------------------------------------------------------

/**
 * Does this visual show a quantity against something else, rather than
 * reporting a lone value with nothing to compare it to? This replaces the old
 * "countQuantities(visual) >= 3" floor as the eligibility gate.
 *
 * That floor had two problems in practice. First, countQuantities hardcoded
 * `case 'gap': return 2`, so a gap chart could never clear a floor of 3 no
 * matter what it plotted — banning a whole kind outright regardless of
 * content. Second, the natural two-group comparison (kvinnor vs. män,
 * utsatta vs. ej utsatta, faktiskt vs. rekommenderat) is the dominant finding
 * shape in survey and statistics reports, and a strict row-count floor of 3
 * rejects it purely for having exactly two rows — even though "two things,
 * compared" is exactly the shape a reader needs to see plotted.
 *
 * True when ANY of these hold:
 *   - a benchmark exists (visual.benchmark)
 *   - a target exists (visual.target, e.g. a gap chart)
 *   - there are 2 or more plotted rows, columns, groups or items
 *   - a row carries a range with two endpoints (e.g. hgf-2's "2 600 till
 *     4 300"), even when that's the visual's only row — the two endpoints
 *     are themselves a comparison
 *
 * False when the visual plots a single quantity with nothing to compare it
 * to: a lone bar, a lone column, a lone delta item with no benchmark or
 * target. This is a chartable-at-all filter, not a quality score — points are
 * independent (see the file-level note above), so there is no ranking of one
 * eligible point against another; departureScore reports an advisory
 * strength signal for a point alongside its own eligibility, nothing more.
 *
 * `steps` is sequential, not a comparison shape, so it always returns false
 * here — though in practice this never changes anything, because `steps`
 * also fails the separate `spatial` check in evaluatePoint's eligibility
 * gate.
 */
export function hasComparison(visual) {
  if (!visual || typeof visual !== 'object') return false;
  if (visual.benchmark) return true;
  if (visual.target !== undefined && visual.target !== null) return true;

  switch (visual.kind) {
    case 'columns': {
      const flatCount = Array.isArray(visual.columns) ? visual.columns.length : 0;
      const groupCount = Array.isArray(visual.groups) ? visual.groups.length : 0;
      return flatCount + groupCount >= 2;
    }
    case 'bars':
    case 'split': {
      const rows = Array.isArray(visual.rows) ? visual.rows : [];
      if (rows.length >= 2) return true;
      return rows.some((row) => Array.isArray(row?.range) && row.range.length === 2);
    }
    case 'delta': {
      const items = Array.isArray(visual.items) ? visual.items : [];
      return items.length >= 2;
    }
    case 'gap':
      // A gap with no target was already handled by the top-level target
      // check; without one, there's nothing to compare the value against.
      return false;
    case 'steps':
      // Sequential, not a comparison shape — see doc comment above.
      return false;
    default:
      return false;
  }
}

// --- relationType -------------------------------------------------------------

/**
 * 'spatial' for the comparisons a reader parses by looking at position or
 * length (columns, bars, gap, delta, split); 'sequential' for steps, which
 * are read in order rather than compared at a glance.
 */
export function relationType(visual) {
  if (!visual || typeof visual !== 'object') return 'unknown';
  switch (visual.kind) {
    case 'columns':
    case 'bars':
    case 'gap':
    case 'delta':
    case 'split':
      return 'spatial';
    case 'steps':
      return 'sequential';
    default:
      return 'unknown';
  }
}

// --- drawnStrings -------------------------------------------------------------

/**
 * Every string the template would actually put on screen for this visual:
 * title, benchmark.label, legend labels, footnote, every valueLabel, every
 * row/column/step/item label, status, note, tag, step lines, a grouped
 * column chart's series (legend) labels, and a delta item's word. Skips
 * null/undefined. Field names that don't literally exist on a kind (delta's
 * "value" instead of "valueLabel", its "period" instead of "label") are
 * mapped onto the closest matching role — see the per-kind cases below —
 * because the point of this function is "what text renders", not "which JS
 * property held it".
 */
export function drawnStrings(visual) {
  const out = [];
  if (!visual || typeof visual !== 'object') return out;
  if (isNonEmptyString(visual.title)) out.push(visual.title);
  if (isNonEmptyString(visual.benchmark?.label)) out.push(visual.benchmark.label);
  // A legend maps a role (e.g. 'actual'/'ref') to a drawn label — applies to
  // any kind that codes rows/columns by role, not just the bars point that
  // happens to use it today (hsb-2).
  for (const entry of visual.legend || []) {
    if (isNonEmptyString(entry?.label)) out.push(entry.label);
  }
  // A footnote is a page-level annotation under the chart (see hgf-3's
  // split), not specific to one kind, so it's handled here rather than in a
  // per-kind case.
  if (isNonEmptyString(visual.footnote)) out.push(visual.footnote);

  switch (visual.kind) {
    case 'columns': {
      for (const col of visual.columns || []) {
        if (isNonEmptyString(col?.label)) out.push(col.label);
        if (isNonEmptyString(col?.valueLabel)) out.push(col.valueLabel);
        // A tag is a small drawn badge on the column itself, e.g. bygg-1's
        // 'prognos' marker on forecast years.
        if (isNonEmptyString(col?.tag)) out.push(col.tag);
      }
      // series is the legend for a grouped-columns chart (paired series
      // labels like 'Hyresrätter'/'Bostadsrätter' in bygg-3, or
      // 'Boverket, juni 2025'/'Kommunerna, ...' in hgf-1) — drawn on screen
      // same as visual.legend above, just named differently for this kind.
      for (const series of visual.series || []) {
        if (isNonEmptyString(series?.label)) out.push(series.label);
      }
      for (const group of visual.groups || []) {
        if (isNonEmptyString(group?.label)) out.push(group.label);
        for (const v of group?.values || []) {
          if (isNonEmptyString(v?.valueLabel)) out.push(v.valueLabel);
        }
      }
      break;
    }
    case 'bars':
    case 'split': {
      for (const row of visual.rows || []) {
        if (isNonEmptyString(row?.label)) out.push(row.label);
        if (isNonEmptyString(row?.valueLabel)) out.push(row.valueLabel);
        if (isNonEmptyString(row?.note)) out.push(row.note);
      }
      break;
    }
    case 'delta': {
      for (const item of visual.items || []) {
        // items have no literal "label"/"valueLabel" fields; period plays the
        // label role and value plays the valueLabel role.
        if (isNonEmptyString(item?.period)) out.push(item.period);
        if (isNonEmptyString(item?.value)) out.push(item.value);
        // word completes the sentence next to the value (e.g. '154 %'
        // + 'högre'), so it's drawn text too.
        if (isNonEmptyString(item?.word)) out.push(item.word);
      }
      break;
    }
    case 'gap': {
      if (isNonEmptyString(visual.valueLabel)) out.push(visual.valueLabel);
      if (isNonEmptyString(visual.targetLabel)) out.push(visual.targetLabel);
      if (isNonEmptyString(visual.valueName)) out.push(visual.valueName);
      if (isNonEmptyString(visual.targetName)) out.push(visual.targetName);
      // gapNote plays the "note" role for this kind.
      if (isNonEmptyString(visual.gapNote)) out.push(visual.gapNote);
      break;
    }
    case 'steps': {
      for (const step of visual.steps || []) {
        if (isNonEmptyString(step?.label)) out.push(step.label);
        if (isNonEmptyString(step?.status)) out.push(step.status);
        if (isNonEmptyString(step?.note)) out.push(step.note);
        for (const line of step?.lines || []) {
          if (isNonEmptyString(line)) out.push(line);
        }
      }
      break;
    }
    default:
      break;
  }
  return out;
}

/** The subset of drawnStrings that represent a drawn *value* (as opposed to a
 * label/status/note), used by addsNewInformation. Kept separate from
 * drawnStrings because "is this a new number" only makes sense for the
 * number-bearing fields, not for labels like axis captions. */
function drawnValueLabels(visual) {
  const out = [];
  if (!visual || typeof visual !== 'object') return out;
  switch (visual.kind) {
    case 'columns':
      for (const col of visual.columns || []) {
        if (isNonEmptyString(col?.valueLabel)) out.push(col.valueLabel);
      }
      for (const group of visual.groups || []) {
        for (const v of group?.values || []) {
          if (isNonEmptyString(v?.valueLabel)) out.push(v.valueLabel);
        }
      }
      break;
    case 'bars':
    case 'split':
      for (const row of visual.rows || []) {
        if (isNonEmptyString(row?.valueLabel)) out.push(row.valueLabel);
      }
      break;
    case 'delta':
      for (const item of visual.items || []) {
        if (isNonEmptyString(item?.value)) out.push(item.value);
      }
      break;
    case 'gap':
      if (isNonEmptyString(visual.valueLabel)) out.push(visual.valueLabel);
      if (isNonEmptyString(visual.targetLabel)) out.push(visual.targetLabel);
      break;
    default:
      break;
  }
  return out;
}

// --- classifyDrawnStrings -----------------------------------------------------

/**
 * A drawn string is a DATA string when it contains at least one digit
 * (a plotted value, a value label like 'omkring 9 300', a benchmark's '36
 * 000'), and a FURNITURE string otherwise (a chart title, a series or
 * legend name, a category label like 'Vatten' or 'I år'). Furniture is
 * authored editorial framing: it is never expected to appear anywhere in the
 * prose the way a value copied verbatim from the source report is, and it is
 * subject only to the word budgets in wordBudgetViolations. A data string is
 * exempt from every word budget — see wordBudgetViolations — because a
 * plotted value must never be trimmed to fit an editorial limit.
 */
function isDataString(value) {
  return typeof value === 'string' && /\d/.test(value);
}

/**
 * Splits every string drawnStrings(visual) would put on screen into { data,
 * furniture } using the digit rule documented on isDataString above.
 */
export function classifyDrawnStrings(visual) {
  const data = [];
  const furniture = [];
  for (const raw of drawnStrings(visual)) {
    if (isDataString(raw)) data.push(raw);
    else furniture.push(raw);
  }
  return { data, furniture };
}

// --- verbatimViolations -------------------------------------------------------

/**
 * Which drawn strings are NOT a contiguous substring of impact, lead, quote
 * or detail — the prose a reader actually reads. Comparison collapses
 * whitespace runs and trims, but does not lowercase or strip punctuation —
 * the whole point is to catch a number or a word that was quietly changed
 * while re-cutting prose, so case and punctuation must match exactly.
 *
 * Returns { notInProse }. This used to also return `notInAnyAuthoredField`,
 * a wider check against hero/facts/headline/citationShort too, that gated
 * eligibility. It was removed — see the block comment at the top of this
 * file for why: it fought `addsNewInformation` head-on, and its real target
 * (does this figure actually appear, verbatim, in the SOURCE REPORT) isn't
 * something either haystack could ever answer, because hero/facts/prose are
 * themselves a re-cut of the report, not the report. CI cannot open the
 * source PDF, so `notInProse` stays purely descriptive: a high count on an
 * otherwise-eligible point means the chart introduces figures the copy never
 * puts into a sentence, which is useful editorial signal, but whether those
 * figures are faithful to the report is — and can only be — a human
 * editorial guarantee.
 */
export function verbatimViolations(point) {
  if (!point || typeof point !== 'object') {
    return { notInProse: [] };
  }

  const proseParts = [];
  if (isNonEmptyString(point.impact)) proseParts.push(point.impact);
  if (isNonEmptyString(point.lead)) proseParts.push(point.lead);
  if (isNonEmptyString(point.quote)) proseParts.push(point.quote);
  if (isNonEmptyString(point.detail)) proseParts.push(point.detail);
  const proseHaystack = normalizeWs(proseParts.join(' ␟ '));

  const drawn = drawnStrings(point.visual);
  const notInProse = [];
  const seenNotInProse = new Set();

  for (const raw of drawn) {
    const needle = normalizeWs(raw);
    if (!needle) continue;
    if (!proseHaystack.includes(needle) && !seenNotInProse.has(needle)) {
      notInProse.push(raw);
      seenNotInProse.add(needle);
    }
  }

  return { notInProse };
}

// --- wordBudgetViolations ------------------------------------------------------

const LABEL_WORD_LIMIT = 3;
const STATUS_WORD_LIMIT = 4;
const STEP_LINE_WORD_LIMIT = 8;
const STEP_LINE_COUNT_LIMIT = 2;
const BENCHMARK_LIMIT = 1;

function pushIfOverLimit(out, field, value, limit) {
  const actual = wordCount(value);
  if (actual > limit) {
    out.push({ field, value, limit, actual });
  }
}

/**
 * The label word budget (LABEL_WORD_LIMIT) applies only to furniture strings
 * — see classifyDrawnStrings' doc comment. A data string, e.g. a value label
 * like 'omkring 9 300', must never be trimmed to fit an editorial word
 * budget, so it's exempt from this check entirely rather than merely given a
 * higher limit.
 */
function pushLabelIfOverLimit(out, value, limit) {
  if (isDataString(value)) return;
  pushIfOverLimit(out, 'label', value, limit);
}

/**
 * Editorial word budgets: a label at most 3 words, a status at most 4 words,
 * a step line at most 8 words, at most 2 lines per step panel, and a chart
 * carries at most one benchmark. Returns [{ field, value, limit, actual }].
 */
export function wordBudgetViolations(visual) {
  const out = [];
  if (!visual || typeof visual !== 'object') return out;

  switch (visual.kind) {
    case 'columns': {
      for (const col of visual.columns || []) {
        if (isNonEmptyString(col?.label)) pushLabelIfOverLimit(out, col.label, LABEL_WORD_LIMIT);
      }
      for (const group of visual.groups || []) {
        if (isNonEmptyString(group?.label)) {
          pushLabelIfOverLimit(out, group.label, LABEL_WORD_LIMIT);
        }
      }
      break;
    }
    case 'bars':
    case 'split': {
      for (const row of visual.rows || []) {
        if (isNonEmptyString(row?.label)) pushLabelIfOverLimit(out, row.label, LABEL_WORD_LIMIT);
      }
      break;
    }
    case 'delta': {
      for (const item of visual.items || []) {
        if (isNonEmptyString(item?.period)) {
          pushLabelIfOverLimit(out, item.period, LABEL_WORD_LIMIT);
        }
      }
      break;
    }
    case 'steps': {
      for (const step of visual.steps || []) {
        if (isNonEmptyString(step?.label)) pushLabelIfOverLimit(out, step.label, LABEL_WORD_LIMIT);
        if (isNonEmptyString(step?.status)) {
          pushIfOverLimit(out, 'status', step.status, STATUS_WORD_LIMIT);
        }
        const lines = Array.isArray(step?.lines) ? step.lines : [];
        for (const line of lines) {
          if (isNonEmptyString(line)) pushIfOverLimit(out, 'step line', line, STEP_LINE_WORD_LIMIT);
        }
        if (lines.length > STEP_LINE_COUNT_LIMIT) {
          out.push({
            field: 'step lines',
            value: lines,
            limit: STEP_LINE_COUNT_LIMIT,
            actual: lines.length,
          });
        }
      }
      break;
    }
    case 'gap':
    default:
      break;
  }

  const benchmarkCount = Array.isArray(visual.benchmarks)
    ? visual.benchmarks.length
    : visual.benchmark
      ? 1
      : 0;
  if (benchmarkCount > BENCHMARK_LIMIT) {
    out.push({
      field: 'benchmark',
      value: visual.benchmarks || visual.benchmark,
      limit: BENCHMARK_LIMIT,
      actual: benchmarkCount,
    });
  }

  return out;
}

// --- labelConsistencyViolations -------------------------------------------------

/**
 * A Swedish-style thousands-grouped rendering of an integer: 9300 -> '9 300',
 * 400 -> '400', -2 -> '-2'. Used to check that a range's two endpoints are
 * both actually written into its valueLabel (e.g. '2 600 till 4 300' for the
 * range [2600, 4300]).
 */
function formatSwedishInt(n) {
  const sign = n < 0 ? '-' : '';
  const digits = String(Math.round(Math.abs(n)));
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return sign + grouped;
}

/**
 * A single plotted value must parse, via parseNumber, to the same number its
 * own valueLabel does. parseNumber already does exactly what the spec here
 * needs: it finds the first digit run in the string, so a leading hedge word
 * (omkring, cirka, drygt, ungefär — none of which contain digits) is already
 * skipped, and a trailing '%' or unit is already ignored because nothing
 * after the digit run is consumed. Pushes nothing when either side is
 * missing or unparseable — that's a data-shape problem, not a label/value
 * mismatch, and outside this check's job.
 */
function checkSingleValueLabel(out, field, rawValue, label) {
  if (!isNonEmptyString(label)) return;
  const expected = parseNumber(rawValue);
  if (expected === null) return;
  const parsed = parseNumber(label);
  if (parsed === null || parsed !== expected) {
    out.push({ field, label, parsed, expected });
  }
}

/**
 * A range item's valueLabel (e.g. 'ungefär 1 300 till 1 400') must contain
 * BOTH endpoints, each written the way an authored Swedish figure is —
 * thousands grouped with a space. Unlike the single-value case there's no
 * one number to parse the label down to, so this checks containment of both
 * formatted endpoints instead.
 */
function checkRangeLabel(out, field, range, label) {
  if (!isNonEmptyString(label) || !Array.isArray(range) || range.length !== 2) return;
  const lo = parseNumber(range[0]);
  const hi = parseNumber(range[1]);
  if (lo === null || hi === null) return;
  const loText = formatSwedishInt(lo);
  const hiText = formatSwedishInt(hi);
  const normalized = normalizeWs(label);
  if (!normalized.includes(loText) || !normalized.includes(hiText)) {
    out.push({ field, label, parsed: normalized, expected: `${loText} and ${hiText}` });
  }
}

/**
 * Every number embedded anywhere in a string, in authoring order. Unlike
 * parseNumber (which returns only the FIRST digit run — the right behaviour
 * for a dedicated value label), a benchmark label is a descriptive phrase
 * that embeds its figure inside prose, e.g. '2000-talets genomsnitt, 36
 * 000' — the first digit run there is '2000', from '2000-talets', not the
 * plotted value at all. Reuses the exact digit-run pattern parseNumber uses
 * (Swedish thousands spaces, comma decimals) so the two stay in lockstep,
 * just applied globally instead of stopping at the first match.
 */
function extractAllNumbers(raw) {
  if (typeof raw !== 'string') return [];
  const matches = raw.match(/-?\d[\d\s ]*(?:,\d+)?/g) || [];
  return matches
    .map((m) => {
      const cleaned = m.replace(/[\s ]/g, '').replace(',', '.');
      const num = parseFloat(cleaned);
      return Number.isNaN(num) ? null : num;
    })
    .filter((n) => n !== null);
}

/**
 * A benchmark's label is a descriptive phrase that embeds its figure inside
 * prose rather than being a dedicated value label (see labelConsistency
 * Violations' doc comment on why the benchmark case used to be skipped
 * entirely). Naive first-digit-run parsing (parseNumber) would grab the
 * wrong number out of a phrase like '2000-talets genomsnitt, 36 000' — 2000,
 * from '2000-talets', not the plotted 36000. This instead pulls out EVERY
 * number in the label and passes as soon as ANY of them equals the plotted
 * benchmark value, after the same Swedish-number normalisation parseNumber
 * uses. Only reports a violation when none of the label's numbers match.
 */
function checkBenchmarkLabel(out, field, rawValue, label) {
  if (!isNonEmptyString(label)) return;
  const expected = parseNumber(rawValue);
  if (expected === null) return;
  const found = extractAllNumbers(label);
  if (!found.some((n) => n === expected)) {
    out.push({ field, label, parsed: found, expected });
  }
}

/**
 * The check CI can genuinely make in place of the unenforceable
 * verbatim-against-the-source check: for every plotted item that carries
 * both a numeric value and a drawn value label, does the label's own number
 * agree with the number it's plotted from? This catches the failure that
 * actually matters — a bar drawn at one height and labelled with a
 * different number — regardless of whether that number is faithful to the
 * source report (a question CI cannot answer; see the file-level comment).
 *
 * Covers all six visual kinds:
 *   - columns: each flat column's value/valueLabel, and each grouped
 *     column's value/valueLabel. A columns chart's `benchmark` IS checked
 *     too, via checkBenchmarkLabel: its label is an editorial annotation
 *     ('2000-talets genomsnitt, 36 000') that embeds the figure inside a
 *     phrase rather than being the figure's own dedicated label, so instead
 *     of parsing "the" number out of the label (which would grab 2000 out
 *     of '2000-talets'), it extracts every number in the label and passes
 *     when any of them equals the plotted benchmark value.
 *   - bars / split: each row's value (or share, for split) against its
 *     valueLabel, or — for a range row like hgf-2's — both endpoints of
 *     row.range against valueLabel via checkRangeLabel.
 *   - gap: value/valueLabel and target/targetLabel.
 *   - delta: nothing to check. An item's `value` field (e.g. '154 %') IS
 *     its own label — there's no separate authored number to reconcile it
 *     against, unlike every other kind where value and valueLabel are two
 *     distinct fields.
 *   - steps: nothing to check. Steps carries no plotted numeric values at
 *     all (label/status/lines are all prose).
 *
 * Returns [{ field, label, parsed, expected }] for every mismatch.
 */
export function labelConsistencyViolations(visual) {
  const out = [];
  if (!visual || typeof visual !== 'object') return out;

  switch (visual.kind) {
    case 'columns': {
      (visual.columns || []).forEach((col, i) => {
        const field = isNonEmptyString(col?.label) ? `column "${col.label}"` : `column[${i}]`;
        checkSingleValueLabel(out, field, col?.value, col?.valueLabel);
      });
      (visual.groups || []).forEach((group, gi) => {
        const groupName = isNonEmptyString(group?.label) ? group.label : `group[${gi}]`;
        (group?.values || []).forEach((v, vi) => {
          checkSingleValueLabel(out, `${groupName} value[${vi}]`, v?.value, v?.valueLabel);
        });
      });
      if (visual.benchmark) {
        checkBenchmarkLabel(out, 'benchmark', visual.benchmark.value, visual.benchmark.label);
      }
      break;
    }
    case 'bars':
    case 'split': {
      (visual.rows || []).forEach((row, i) => {
        const field = isNonEmptyString(row?.label) ? `row "${row.label}"` : `row[${i}]`;
        if (Array.isArray(row?.range)) {
          checkRangeLabel(out, field, row.range, row?.valueLabel);
        } else {
          const raw = row?.value !== undefined ? row.value : row?.share;
          checkSingleValueLabel(out, field, raw, row?.valueLabel);
        }
      });
      break;
    }
    case 'gap': {
      checkSingleValueLabel(out, 'value', visual.value, visual.valueLabel);
      checkSingleValueLabel(out, 'target', visual.target, visual.targetLabel);
      break;
    }
    case 'delta':
      // item.value IS the label — see the doc comment above.
      break;
    case 'steps':
      // No plotted numeric values on this kind — see the doc comment above.
      break;
    default:
      break;
  }

  return out;
}

// --- citationConsistencyViolations -----------------------------------------------

/**
 * Every number embedded in a string, in authoring order — same digit-run
 * pattern as extractAllNumbers above but without the Swedish thousands-space
 * handling, because citation fields (page numbers, "s. 5 och 7") never carry
 * grouped thousands.
 */
function extractPageNumbers(raw) {
  if (typeof raw !== 'string') return [];
  const matches = raw.match(/\d+/g) || [];
  return matches.map(Number);
}

/**
 * CI cannot open the source PDF, so it cannot check the one defect that
 * actually matters — whether the cited page is the page that carries the
 * quoted material (see the file-level comment on verbatimViolations for the
 * same limit applied to figures). What it CAN check, cheaply, is that a
 * point's three citation fields agree with each other: citation.url often
 * carries a page anchor (`#page=N`, e.g. for a "jump straight to the page"
 * link), and when it does, that same page number N should also show up in
 * citation.pages (the human-readable page reference, e.g. 's. 5 och 7 i
 * pdf:en') and in citation.linkLabel (the link text, e.g. 'Öppna pdf:en på
 * s. 5'). A mismatch there is typo drift — someone changed the anchor, or
 * the pages note, or the link label, and not the other two — which is a real
 * editorial defect even though it is a different (and much cheaper to catch)
 * defect than "is this the right page at all".
 *
 * This is reported signal only. It is deliberately NOT wired into
 * evaluatePoint's `eligible` gate: a citation problem is an editorial defect
 * on the point's sourcing, not a reason to withhold an otherwise-sound
 * visual from a reader.
 *
 * Returns [] when there's no citation, no url, or no page anchor on the url
 * (nothing to cross-check against). Otherwise returns one violation object
 * per field that doesn't mention the anchored page number.
 */
export function citationConsistencyViolations(point) {
  const out = [];
  const citation = point?.citation;
  if (!citation || typeof citation !== 'object') return out;
  if (typeof citation.url !== 'string') return out;

  const anchorMatch = citation.url.match(/#page=(\d+)/);
  if (!anchorMatch) return out;
  const anchor = Number(anchorMatch[1]);

  const pageNumbers = extractPageNumbers(citation.pages);
  if (!pageNumbers.includes(anchor)) {
    out.push({
      field: 'pages',
      anchor,
      value: citation.pages,
      message: `citation.url anchors page ${anchor}, but citation.pages ("${citation.pages}") does not mention it`,
    });
  }

  const linkLabelNumbers = extractPageNumbers(citation.linkLabel);
  if (!linkLabelNumbers.includes(anchor)) {
    out.push({
      field: 'linkLabel',
      anchor,
      value: citation.linkLabel,
      message: `citation.url anchors page ${anchor}, but citation.linkLabel ("${citation.linkLabel}") points to a different page`,
    });
  }

  return out;
}

// --- addsNewInformation ---------------------------------------------------------

/**
 * True when at least one drawn valueLabel is not already sitting in
 * impact + lead. A visual that only redraws numbers the reader already saw
 * in the headline and lead sentence doesn't earn its space.
 */
export function addsNewInformation(point) {
  if (!point || typeof point !== 'object') return false;
  const haystack = normalizeWs(
    [point.impact, point.lead].filter(isNonEmptyString).join(' ␟ ')
  );
  const values = drawnValueLabels(point.visual);
  return values.some((raw) => {
    const needle = normalizeWs(raw);
    return needle && !haystack.includes(needle);
  });
}

// --- headlineValue / referenceValue / departureScore -----------------------
//
// departureScore used to be three different measurements normalised to the
// same 0–1 range and then compared as if they meant the same thing: external
// departure from a stated reference (gap, and columns with a benchmark),
// internal spread among plotted values (bars, split, flat columns, grouped
// columns), and raw magnitude (delta, abs(value)/100). Ranking across kinds
// with those was not meaningful, and the columns-with-benchmark case had a
// real bug: it took the MAXIMUM deviation across every plotted value, so a
// point could lose to (or, as with hgf-1 vs. hgf-2, be out-ranked by) a
// column the point was never making a claim about.
//
// The fix is one formula, one question: how far is THE HEADLINE PLOTTED
// VALUE — the figure the point is actually about — from ITS OWN stated
// reference, in relative terms. headlineValue and referenceValue each
// identify one plotted item; departureScore just compares the two numbers
// they resolve to. Every per-kind branch that used to synthesize a "spread"
// or a "magnitude" instead of a genuine reference is gone.

function clamp01(n) {
  if (Number.isNaN(n)) return null;
  return Math.max(0, Math.min(1, n));
}

/**
 * Every plotted item that carries a single scalar number, across all six
 * visual kinds, as { value, label, role, raw, kind, groupLabel, siblings }.
 *
 * `raw` is the authored object the item came from (a column, a row, a delta
 * item, or a grouped column's value entry), so a caller can read its own
 * `label`/`period` for a human-readable name. `siblings` is set only for a
 * grouped-columns value: the other wrapped items in the same group, so
 * referenceValue can find "the other value in this group" without having to
 * re-walk visual.groups itself.
 *
 * A benchmark or a target is deliberately NOT included here: those are
 * reference-only concepts (see referenceValue), never a headline candidate.
 *
 * A bars/split row that carries only a `range` (two endpoints, no single
 * `value`/`share`) is skipped rather than collapsed into an invented
 * midpoint — the EDITORIAL CONTRACT at the top of src/config/rapportvakt.js
 * is explicit that a number here is never recomputed or invented, and a
 * range's two endpoints are not "a plotted item's numeric value" in the
 * sense headlineValue needs. A point built entirely from range rows (e.g.
 * hgf-2) correctly has no scalar candidate at all — see the file-level note
 * on departureScore below.
 */
function plottedItems(visual) {
  const items = [];
  if (!visual || typeof visual !== 'object') return items;

  switch (visual.kind) {
    case 'columns': {
      for (const col of visual.columns || []) {
        const value = parseNumber(col?.value);
        if (value !== null) {
          items.push({ value, label: col?.valueLabel ?? null, role: col?.role, raw: col, kind: 'column', groupLabel: null });
        }
      }
      for (const group of visual.groups || []) {
        const groupItems = [];
        for (const val of group?.values || []) {
          const value = parseNumber(val?.value);
          if (value !== null) {
            groupItems.push({
              value,
              label: val?.valueLabel ?? null,
              role: val?.role,
              raw: val,
              kind: 'group-value',
              groupLabel: group?.label ?? null,
            });
          }
        }
        for (const item of groupItems) item.siblings = groupItems;
        items.push(...groupItems);
      }
      break;
    }
    case 'bars':
    case 'split': {
      for (const row of visual.rows || []) {
        const rawNumber = row?.value !== undefined ? row.value : row?.share;
        const value = parseNumber(rawNumber);
        if (value !== null) {
          items.push({ value, label: row?.valueLabel ?? null, role: row?.role, raw: row, kind: 'row', groupLabel: null });
        }
      }
      break;
    }
    case 'delta': {
      for (const item of visual.items || []) {
        const value = parseNumber(item?.value);
        if (value !== null) {
          items.push({ value, label: item?.value ?? null, role: item?.role, raw: item, kind: 'delta-item', groupLabel: item?.period ?? null });
        }
      }
      break;
    }
    case 'gap': {
      const value = parseNumber(visual.value);
      if (value !== null) {
        items.push({ value, label: visual.valueLabel ?? null, role: undefined, raw: visual, kind: 'gap-value', groupLabel: null });
      }
      break;
    }
    default:
      break;
  }
  return items;
}

/**
 * Identifies the plotted item a point is actually about: point.hero.value
 * is the dominant figure, authored by a human, so this parses it with
 * parseNumber and finds the plotted item (see plottedItems above) whose
 * numeric value matches it exactly. Falls back, in order, to the item whose
 * role is 'actual', then the last plotted item, then null.
 *
 * Returns { value, source }. `source` is the plottedItems() wrapper the
 * value came from (or null), kept around so referenceValue can find "the
 * other item in this comparison" and so a report can show which item was
 * picked.
 */
export function headlineValue(point) {
  const visual = point?.visual;
  const items = plottedItems(visual);
  const heroNumber = parseNumber(point?.hero?.value);

  if (heroNumber !== null) {
    const match = items.find((item) => item.value === heroNumber);
    if (match) return { value: match.value, source: match };
  }

  const actualMatch = items.find((item) => item.role === 'actual');
  if (actualMatch) return { value: actualMatch.value, source: actualMatch };

  if (items.length > 0) {
    const last = items[items.length - 1];
    return { value: last.value, source: last };
  }

  return { value: null, source: null };
}

/** The label an item's role carries in visual.legend or visual.series (a
 * grouped-columns/bars chart's authored name for that role, e.g. hsb-2's
 * 'Driften som helhet' for role 'ref'), or null when neither exists or
 * neither names this item's role. */
function roleLabelFor(visual, item) {
  if (!item?.role) return null;
  for (const collection of [visual?.legend, visual?.series]) {
    if (!Array.isArray(collection)) continue;
    const entry = collection.find((e) => e?.role === item.role);
    if (entry && isNonEmptyString(entry.label)) return entry.label;
  }
  return null;
}

/** A human-readable name for a plotted item when no role label applies:
 * its own authored label/period, e.g. hgf-2's 'Hela 2024' or bra-1's '2014'. */
function ownLabelFor(item) {
  if (isNonEmptyString(item?.raw?.label)) return item.raw.label;
  if (isNonEmptyString(item?.raw?.period)) return item.raw.period;
  return null;
}

/**
 * Finds what the headline value is measured against, in priority order:
 *   1. an explicit visual.benchmark.value
 *   2. an explicit visual.target
 *   3. in a two-item comparison, the OTHER item — scoped to the headline's
 *      own group when it belongs to a two-value group (e.g. hgf-1's or
 *      bygg-3's per-period actual/ref pair), otherwise the other item in a
 *      flat two-item list (e.g. a two-row bars chart)
 *   4. in a longer list, the item whose role is 'ref' or 'past', otherwise
 *      the item immediately preceding the headline in authoring order
 *
 * Returns { value, label }, where label names what the reference is (e.g.
 * 'benchmark', 'target', or an authored name pulled from the legend/series
 * or the other item's own label). Returns { value: null, label: null } when
 * nothing qualifies — a genuinely unrankable point, not a formula gap.
 */
export function referenceValue(point) {
  const visual = point?.visual;
  if (!visual || typeof visual !== 'object') return { value: null, label: null };

  const benchmarkValue = parseNumber(visual.benchmark?.value);
  if (benchmarkValue !== null) return { value: benchmarkValue, label: 'benchmark' };

  const targetValue = parseNumber(visual.target);
  if (targetValue !== null) return { value: targetValue, label: 'target' };

  const headline = headlineValue(point);
  if (!headline.source) return { value: null, label: null };
  // headline.source came from headlineValue's own internal plottedItems()
  // call. Every comparison below identifies "the same item" by its raw
  // authored object (stable across separate plottedItems() calls on the
  // same visual), never by wrapper-object identity (a fresh wrapper is
  // allocated on every call, so `item !== headline.source` would always be
  // true and silently pick the wrong — or no — item).
  const headlineRaw = headline.source.raw;

  const siblings = headline.source.siblings;
  if (Array.isArray(siblings) && siblings.length === 2) {
    const other = siblings.find((item) => item.raw !== headlineRaw);
    if (other) {
      return { value: other.value, label: roleLabelFor(visual, other) ?? ownLabelFor(other) ?? 'other group' };
    }
  }

  const items = plottedItems(visual);

  if (items.length === 2) {
    const other = items.find((item) => item.raw !== headlineRaw);
    if (other) {
      return { value: other.value, label: roleLabelFor(visual, other) ?? ownLabelFor(other) ?? 'other item' };
    }
  }

  if (items.length > 2) {
    const roleMatch = items.find(
      (item) => item.raw !== headlineRaw && (item.role === 'ref' || item.role === 'past')
    );
    if (roleMatch) {
      return { value: roleMatch.value, label: roleLabelFor(visual, roleMatch) ?? ownLabelFor(roleMatch) ?? 'previous period' };
    }
    const index = items.findIndex((item) => item.raw === headlineRaw);
    if (index > 0) {
      const previous = items[index - 1];
      return { value: previous.value, label: ownLabelFor(previous) ?? 'previous period' };
    }
  }

  return { value: null, label: null };
}

/**
 * How far the headline figure sits from its own stated reference, as a
 * number from 0 to 1, or null when it can't be computed: an unknown or
 * sequential (`steps`) kind, a headline with no matching plotted item, or a
 * reference nothing qualifies for (see referenceValue). Never throws on
 * missing fields.
 *
 * This is an ADVISORY signal only — see the file-level note above. It is
 * shown to the author alongside an eligible point so a human can weigh it
 * before confirming, never used to rank one point against another and never
 * read by `evaluatePoint`'s `eligible` gate. A null score must never block
 * eligibility or rendering.
 *
 * Two points in the corpus resolve to a genuinely null score on purpose —
 * see the invariant test in tests/rapportvaktVisualRules.test.js for why
 * hgf-2 and trafa-3 specifically have no computable departure, and why that
 * is a fact about what those points plot rather than a gap in this formula.
 */
export function departureScore(point) {
  const visual = point?.visual;
  if (!visual || typeof visual !== 'object' || visual.kind === 'steps') return null;

  const headline = headlineValue(point);
  if (headline.value === null) return null;

  const reference = referenceValue(point);
  if (reference.value === null) return null;

  return clamp01(Math.abs(headline.value - reference.value) / Math.abs(reference.value));
}

// --- evaluatePoint / evaluateDelivery --------------------------------------------

/**
 * Runs every check against one point and combines them into a single
 * eligibility verdict. Points are independent — see the file-level note
 * above — so this never looks at any other point in the delivery.
 *
 * `optIn` reports the raw `visual.show` flag as a tri-state: `true` (the
 * author forced it on), `false` (the author forced it off), or `null` (no
 * flag authored — the point is relying on the default verdict). It is
 * reported for visibility only and never read by `eligible`: the flag's
 * actual effect on rendering lives in `visualOutcome`, not here.
 */
export function evaluatePoint(point) {
  const visual = point?.visual;
  const kind = visual?.kind ?? null;
  const quantities = countQuantities(visual);
  const relation = relationType(visual);
  const comparison = hasComparison(visual);
  const spatial = relation === 'spatial';
  const addsNew = addsNewInformation(point);
  const showFlag = visual?.show;
  const optIn = showFlag === true ? true : showFlag === false ? false : null;
  const { notInProse } = verbatimViolations(point);
  const labelViolations = labelConsistencyViolations(visual);
  const budgetViolations = wordBudgetViolations(visual);
  const citationViolations = citationConsistencyViolations(point);
  const score = departureScore(point);

  // labelConsistencyViolations and wordBudgetViolations gate eligibility;
  // notInProse and citationConsistencyViolations are reported alongside them
  // purely as editorial signal (see verbatimViolations' and
  // citationConsistencyViolations' doc comments) and are never a hard
  // failure — a citation problem is a sourcing defect on the point, not a
  // reason to withhold an otherwise-sound visual, and a genuine
  // verbatim-against-the-source-PDF check is a human editorial guarantee,
  // because CI cannot open the PDF to verify one. `score` is likewise never
  // part of this gate — see departureScore's doc comment: an advisory signal
  // must never block an otherwise-qualifying point.
  const eligible =
    comparison &&
    spatial &&
    addsNew &&
    labelViolations.length === 0 &&
    budgetViolations.length === 0;

  return {
    id: point?.id ?? null,
    kind,
    quantities,
    relation,
    hasComparison: comparison,
    spatial,
    addsNew,
    optIn,
    labelConsistencyViolations: labelViolations,
    notInProse,
    wordBudgetViolations: budgetViolations,
    citationConsistencyViolations: citationViolations,
    score,
    eligible,
  };
}

// --- visualOutcome ------------------------------------------------------------

// The only visual kind with a drawn chart today — see PointVisual.jsx's
// ColumnsChart. Kept here as a plain data fact (not a React import) so
// visualOutcome can be tested without touching the component, and so the
// report script can count "would show a chart" the same way the app does.
const RENDERABLE_KINDS = new Set(['columns']);

/** Whether `kind` has a drawn chart implementation today. */
export function hasRenderer(kind) {
  return RENDERABLE_KINDS.has(kind);
}

/**
 * Decides, for one point, whether it renders as a drawn chart, a verdict
 * chip, or nothing at all — the same decision PointVisual.jsx makes. Kept
 * here so it can be tested directly and so PointVisual and the report script
 * agree on exactly one definition of "would show a visual".
 *
 * `point.visual.show`, when present, wins outright over everything else:
 *   - `true`  — render the chart when the kind has a renderer. There is
 *     nothing to force when it doesn't (forcing a chart into existence is
 *     not this function's job), so it falls back to the chip when the point
 *     carries a verdict, same as the no-flag case would.
 *   - `false` — never render anything for this point, chart or chip.
 *
 * With no flag authored, the default applies: a chart when the point is
 * eligible AND its kind has a renderer, a chip when the point carries a
 * verdict but no chart was drawn, otherwise nothing. Note `eligible` alone
 * is not sufficient for a chart — an eligible `bars` or `split` point still
 * falls back to a chip today, because no chart exists yet for those kinds.
 */
export function visualOutcome(point, evaluation) {
  const kind = evaluation?.kind ?? point?.visual?.kind ?? null;
  const renderable = hasRenderer(kind);
  const hasVerdict = isNonEmptyString(point?.verdict?.text);
  const show = point?.visual?.show;

  if (show === true) {
    if (renderable) return 'chart';
    return hasVerdict ? 'chip' : 'none';
  }
  if (show === false) return 'none';

  if (evaluation?.eligible && renderable) return 'chart';
  if (hasVerdict) return 'chip';
  return 'none';
}

/**
 * Evaluates every point on a delivery independently — there is no
 * competition and no winner (see the file-level note above). Returns the
 * per-point evaluations plus simple counts a report can use:
 *   - `eligibleCount` — how many points pass the admission test on their own
 *     merits, regardless of whether their kind has a chart renderer yet.
 *   - `visualCount` — how many points would actually render a drawn chart
 *     today (eligible AND renderable, or forced via `visual.show: true`),
 *     which is the number that matters for "does a delivery feel visually
 *     crowded" now that nothing caps it at one.
 */
export function evaluateDelivery(delivery) {
  const rawPoints = delivery?.points || [];
  const points = rawPoints.map(evaluatePoint);

  let eligibleCount = 0;
  let visualCount = 0;
  points.forEach((evaluated, i) => {
    if (evaluated.eligible) eligibleCount += 1;
    if (visualOutcome(rawPoints[i], evaluated) === 'chart') visualCount += 1;
  });

  return {
    publisher: delivery?.publisher ?? null,
    points,
    eligibleCount,
    visualCount,
  };
}
